// src/components/pages/mypage/hooks/useFollows.js

import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_BASE_URL;

export default function useFollows(auth, handleConfirm, t) {
  const currentUserId = auth.user?.profile?.sub;
  const [followingList, setFollowingList] = useState([]);
  const [followerList, setFollowerList] = useState([]);
  const [loadingFollows, setLoadingFollows] = useState(true);
  const [showFollowListModal, setShowFollowListModal] = useState(false);
  const [followListModalType, setFollowListModalType] = useState(''); 

  const followingMeUrl = `${API_URL}/user/following/${currentUserId}`;
  const followersMeUrl = `${API_URL}/user/followers/${currentUserId}`;
  const followActionUrl = `${API_URL}/user/follow`;

  // 팔로우/팔로워 목록 로드
  useEffect(() => {
    const fetchFollowLists = async () => {
      if (!auth.isAuthenticated || !currentUserId) return;
      setLoadingFollows(true);
      try {
        const [followingRes, followerRes] = await Promise.all([
          fetch(followingMeUrl, { headers: { Authorization: `Bearer ${auth.user?.id_token}` } }),
          fetch(followersMeUrl, { headers: { Authorization: `Bearer ${auth.user?.id_token}` } }),
        ]);

        if (!followingRes.ok) throw new Error(`Failed to fetch following list (${followingRes.status})`);
        if (!followerRes.ok) throw new Error(`Failed to fetch follower list (${followerRes.status})`);

        const followingData = await followingRes.json();
        const followerData = await followerRes.json();

        setFollowingList(followingData.following || []);
        setFollowerList(followerData.followers || []);
      } catch (err) {
        console.error('Failed to load follow lists', err);
      } finally {
        setLoadingFollows(false);
      }
    };

    if (auth.isAuthenticated && currentUserId) {
      fetchFollowLists();
    }
  }, [followingMeUrl, followersMeUrl, auth.isAuthenticated, auth.user?.id_token, currentUserId]);

  // 팔로우
  const handleFollow = useCallback(async (targetUserId, targetUserNickname) => {
    if (!auth.isAuthenticated) {
      alert(t('mypage.alert.followLoginRequired'));
      return;
    }
    try {
      const res = await fetch(followActionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.id_token}` },
        body: JSON.stringify({ targetUserId }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || `Failed to follow user (${res.status})`);
      }

      setFollowingList(prev => [...prev, { userId: targetUserId, nickname: targetUserNickname }]);
      setFollowerList(prev => prev.map(user => user.userId === targetUserId ? { ...user, isFollowing: true } : user));
      alert(t('mypage.alert.followedUser', { nickname: targetUserNickname }));
    } catch (err) {
      console.error('Failed to follow user', err);
      alert(`${t('mypage.error.followFailed')} ${err.message}`);
    }
  }, [auth.isAuthenticated, auth.user?.id_token, followActionUrl, t]);

  // 언팔로우
  const handleUnfollow = useCallback((targetUserId, targetUserNickname) => {
    handleConfirm({
      message: t('mypage.confirm.unfollowUser', { nickname: targetUserNickname }),
      callback: async () => {
        if (!auth.isAuthenticated) {
          alert(t('mypage.alert.unfollowLoginRequired'));
          return;
        }
        try {
          const res = await fetch(followActionUrl, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.id_token}` },
            body: JSON.stringify({ targetUserId }),
          });
          if (!res.ok) {
            const errorData = await res.json();
            throw new Error(errorData.error || `Failed to unfollow user (${res.status})`);
          }
          setFollowingList(prev => prev.filter(u => u.userId !== targetUserId));
          setFollowerList(prev => prev.map(user => user.userId === targetUserId ? { ...user, isFollowing: false } : user));
          alert(t('mypage.alert.unfollowedUser', { nickname: targetUserNickname }));
        } catch (err) {
          console.error('Failed to unfollow user', err);
          alert(`${t('mypage.error.unfollowFailed')} ${err.message}`);
        }
      }
    });
  }, [auth.isAuthenticated, auth.user?.id_token, followActionUrl, handleConfirm, t]);

  const handleOpenFollowListModal = useCallback((type) => {
    setFollowListModalType(type);
    setShowFollowListModal(true);
  }, []);

  const handleCloseFollowListModal = useCallback(() => {
    setShowFollowListModal(false);
    setFollowListModalType('');
  }, []);

  return {
    followingList,
    followerList,
    loadingFollows,
    showFollowListModal,
    followListModalType,
    handleFollow,
    handleUnfollow,
    handleOpenFollowListModal,
    handleCloseFollowListModal,
  };
}